import React, { useState, useMemo, useEffect } from 'react';
import { roleAPI } from '../../services/rbacApi';
import { userAPI } from '../../services/api';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';

export default function AllUsersPermissionMatrix({
  roles,
  onBack,
  searchTerm: initialSearch = '',
  emptyPermissionsOnly = false,
}) {
  const [allUsers, setAllUsers] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsLoadedCount, setAccountsLoadedCount] = useState(0);
  const [resolvedUsers, setResolvedUsers] = useState({});
  const [resolvedCount, setResolvedCount] = useState(0);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialSearch || '');
  const [resourceSearch, setResourceSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [groupBy, setGroupBy] = useState(''); // '', 'employee', 'module', 'resource'
  const [sortBy, setSortBy] = useState('employee'); // 'employee', 'empNo', 'module', 'resource'
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);

  // Load merged accounts on demand (never on parent mount)
  useEffect(() => {
    let active = true;

    const loadAccounts = async () => {
      setAccountsLoading(true);
      setAccountsLoadedCount(0);
      setAllUsers([]);
      setResolvedUsers({});
      setResolvedCount(0);
      setIsFullyLoaded(false);

      const PAGE_LIMIT = 100;
      const all = [];
      let currentPage = 1;

      try {
        while (active) {
          const res = await userAPI.getAccountsReport({
            page: currentPage,
            limit: PAGE_LIMIT,
            includeStats: 'false',
            emptyPermissionsOnly: emptyPermissionsOnly ? 'true' : 'false',
            ...(initialSearch && { search: initialSearch }),
          });
          const rd = res.data?.data || res.data || {};
          const raw = rd.accounts || [];
          if (raw.length === 0) break;

          const mapped = raw.map((a) => ({
            ...a,
            id: a.id || a._id || (a.employeeId ? `emp:${a.employeeId}` : undefined),
            employeeId: a.employeeId ? String(a.employeeId).trim() : '',
            firstName: a.firstName || '',
            lastName: a.lastName || '',
          }));
          all.push(...mapped);
          if (active) {
            setAllUsers([...all]);
            setAccountsLoadedCount(all.length);
          }

          const pages = rd.pagination?.pages || 1;
          if (currentPage >= pages || raw.length < PAGE_LIMIT) break;
          currentPage += 1;
        }
      } catch (err) {
        console.error('Failed to load accounts for matrix:', err);
      } finally {
        if (active) setAccountsLoading(false);
      }
    };

    loadAccounts();
    return () => { active = false; };
  }, [initialSearch, emptyPermissionsOnly]);

  const getRowsByEmployee = () => {
    const groups = {};
    sortedFilteredRows.forEach((row) => {
      const key = row.userId || `${row.employeeId}_${row.userName}`;
      if (!groups[key]) {
        groups[key] = {
          employeeId: row.employeeId,
          userName: row.userName,
          rows: []
        };
      }
      groups[key].rows.push(row);
    });
    return Object.values(groups);
  };

  const handleExportCSV = async () => {
    const dateStr = new Date().toISOString().slice(0, 10);
    const headers = ['Emp No', 'Employee Name', 'Module', 'Resource', 'Permission Name', ...actionColumns];

    const csv = (val) => {
      if (val === null || val === undefined) return '""';
      return `"${String(val).replace(/"/g, '""')}"`;
    };

    if (!searchTerm.trim()) {
      const zip = new JSZip();
      const employees = getRowsByEmployee();

      employees.forEach((emp) => {
        let csvContent = '\uFEFF' + headers.map(csv).join(',') + '\n';
        emp.rows.forEach((row) => {
          const lineData = [
            row.employeeId,
            row.userName,
            row.module,
            row.resource,
            row.permissionName,
            ...actionColumns.map((act) => (row.actions[act] ? 'Y' : '—'))
          ];
          csvContent += lineData.map(csv).join(',') + '\n';
        });

        const safeName = `${emp.employeeId || 'no-id'}_${emp.userName.replace(/\s+/g, '_')}`;
        zip.file(`${safeName}.csv`, csvContent);
      });

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `employees-permission-matrix-csv-${dateStr}.zip`);
    } else {
      let csvContent = '\uFEFF' + headers.map(csv).join(',') + '\n';
      sortedFilteredRows.forEach((row) => {
        const lineData = [
          row.employeeId,
          row.userName,
          row.module,
          row.resource,
          row.permissionName,
          ...actionColumns.map((act) => (row.actions[act] ? 'Y' : '—'))
        ];
        csvContent += lineData.map(csv).join(',') + '\n';
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `all-employees-permission-matrix-${dateStr}.csv`);
    }
  };

  const handleExportPDF = async () => {
    const dateStr = new Date().toISOString().slice(0, 10);

    if (!searchTerm.trim()) {
      const zip = new JSZip();
      const employees = getRowsByEmployee();

      for (const emp of employees) {
        const doc = new jsPDF('l', 'mm', 'a4');
        doc.setFontSize(14);
        doc.text(`Permission Matrix: ${emp.userName} (${emp.employeeId || 'No ID'})`, 14, 15);
        doc.setFontSize(9);
        doc.text(`Generated on: ${dateStr}`, 14, 20);

        const headers = [['Emp No', 'Employee Name', 'Module', 'Resource', 'Permission Name', ...actionColumns]];
        const tableData = emp.rows.map((row) => [
          row.employeeId,
          row.userName,
          row.module,
          row.resource,
          row.permissionName,
          ...actionColumns.map((act) => (row.actions[act] ? 'Y' : '—'))
        ]);

        doc.autoTable({
          startY: 25,
          head: headers,
          body: tableData,
          theme: 'grid',
          headStyles: { fillColor: [79, 70, 229], fontSize: 7, cellPadding: 1.5 },
          bodyStyles: { fontSize: 6.5, cellPadding: 1 },
          styles: { overflow: 'ellipsize' }
        });

        const pdfBlob = doc.output('blob');
        const safeName = `${emp.employeeId || 'no-id'}_${emp.userName.replace(/\s+/g, '_')}`;
        zip.file(`${safeName}.pdf`, pdfBlob);
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `employees-permission-matrix-pdf-${dateStr}.zip`);
    } else {
      const doc = new jsPDF('l', 'mm', 'a4');
      doc.setFontSize(14);
      doc.text("All Employees Permission Matrix", 14, 15);
      doc.setFontSize(9);
      doc.text(`Generated on: ${dateStr}`, 14, 20);

      const headers = [['Emp No', 'Employee Name', 'Module', 'Resource', 'Permission Name', ...actionColumns]];
      const tableData = sortedFilteredRows.map((row) => [
        row.employeeId,
        row.userName,
        row.module,
        row.resource,
        row.permissionName,
        ...actionColumns.map((act) => (row.actions[act] ? 'Y' : '—'))
      ]);

      doc.autoTable({
        startY: 25,
        head: headers,
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [79, 70, 229], fontSize: 7, cellPadding: 1.5 },
        bodyStyles: { fontSize: 6.5, cellPadding: 1 },
        styles: { overflow: 'ellipsize' }
      });

      doc.save(`all-employees-permission-matrix-${dateStr}.pdf`);
    }
  };


  useEffect(() => {
    let active = true;

    const loadAllProgressively = async () => {
      setLoadingRoles(true);
      try {
        // 1. Fetch details of all roles to cache them (use pre-loaded permissions when available)
        const cache = {};
        const fetchPromises = [];

        roles.forEach((r) => {
          if (r.permissions && Array.isArray(r.permissions)) {
            cache[r.code] = r.permissions;
          } else {
            fetchPromises.push(
              (async () => {
                const code = r.code;
                try {
                  const res = await roleAPI.getRole(code);
                  if (active) {
                    cache[code] = res.data?.data?.role?.permissions || [];
                  }
                } catch {
                  if (active) cache[code] = [];
                }
              })()
            );
          }
        });

        if (fetchPromises.length > 0) {
          await Promise.all(fetchPromises);
        }

        if (!active) return;
        setLoadingRoles(false);

        // 2. Resolve permissions progressively in chunks
        const chunkSize = 8;
        for (let i = 0; i < allUsers.length; i += chunkSize) {
          const chunk = allUsers.slice(i, i + chunkSize);
          const chunkResolved = {};

          chunk.forEach((user) => {
            const roleCodes = [];
            if (user.primaryRole) {
              const primaryCode = typeof user.primaryRole === 'string'
                ? user.primaryRole
                : user.primaryRole.roleCode || user.primaryRole.code;
              if (primaryCode) roleCodes.push(primaryCode);
            }
            if (user.roles && Array.isArray(user.roles)) {
              user.roles.forEach((r) => {
                const code = typeof r === 'string' ? r : r.roleCode || r.code;
                if (code && !roleCodes.includes(code)) {
                  roleCodes.push(code);
                }
              });
            }

            const userPermissions = new Map();
            roleCodes.forEach((code) => {
              const perms = cache[code] || [];
              perms.forEach((p) => {
                if (!userPermissions.has(p.code)) {
                  userPermissions.set(p.code, p);
                }
              });
            });

            const grouped = {};
            userPermissions.forEach((p) => {
              const codeStr = p.code || '';
              const parts = codeStr.split(':');
              const module = p.module || parts[0] || 'SYSTEM';
              const resource = parts[1] || 'GENERAL';
              const action = parts[2] || '';

              const key = `${module}:${resource}`;
              if (!grouped[key]) {
                grouped[key] = {
                  module,
                  resource,
                  permissionName: p.name || resource,
                  actions: {},
                };
              }
              if (action) {
                grouped[key].actions[action.toUpperCase()] = true;
              }
            });

            const userName = user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : user.name || user.email || 'Unknown';

            chunkResolved[user.id] = Object.values(grouped).map((item) => ({
              userId: user.id,
              employeeId: user.employeeId || '—',
              userName,
              module: item.module,
              resource: item.resource,
              permissionName: item.permissionName,
              actions: item.actions,
            }));
          });

          if (!active) return;

          setResolvedUsers((prev) => ({ ...prev, ...chunkResolved }));
          setResolvedCount(Math.min(i + chunkSize, allUsers.length));

          // Brief pause to allow UI rendering
          await new Promise((resolve) => setTimeout(resolve, 20));
        }

        if (active) {
          setIsFullyLoaded(true);
        }
      } catch (err) {
        console.error('Failed to load matrix database:', err);
      }
    };

    if (!accountsLoading && roles && roles.length > 0 && allUsers && allUsers.length > 0) {
      loadAllProgressively();
    } else if (!accountsLoading && (!allUsers || allUsers.length === 0)) {
      setLoadingRoles(false);
      setIsFullyLoaded(true);
    }

    return () => {
      active = false;
    };
  }, [allUsers, roles, accountsLoading]);

  // Merge all resolved row arrays
  const matrixRows = useMemo(() => {
    const rows = [];
    Object.values(resolvedUsers).forEach((userRows) => {
      rows.push(...userRows);
    });
    return rows;
  }, [resolvedUsers]);

  // Unique modules list for dropdown filter
  const modulesList = useMemo(() => {
    const mods = new Set();
    matrixRows.forEach((r) => {
      if (r.module) mods.add(r.module);
    });
    return Array.from(mods).sort();
  }, [matrixRows]);

  // Filtered rows
  const filteredRows = useMemo(() => {
    return matrixRows.filter((row) => {
      const matchesUser = !searchTerm ||
        row.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.employeeId.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesResource = !resourceSearch ||
        row.resource.toLowerCase().includes(resourceSearch.toLowerCase()) ||
        row.permissionName.toLowerCase().includes(resourceSearch.toLowerCase());

      const matchesModule = !moduleFilter || row.module === moduleFilter;

      return matchesUser && matchesResource && matchesModule;
    });
  }, [matrixRows, searchTerm, resourceSearch, moduleFilter]);

  // Get all unique actions dynamically from resolved users' permission rows
  const actionColumns = useMemo(() => {
    const standard = ['CREATE', 'READ', 'SHOW', 'UPDATE', 'DELETE', 'APPROVE'];
    const actions = new Set(standard);
    matrixRows.forEach((row) => {
      Object.keys(row.actions).forEach((act) => {
        if (act) actions.add(act);
      });
    });

    const preferredOrder = [
      'CREATE', 'READ', 'SHOW', 'UPDATE', 'DELETE', 'APPROVE', 'VIEW', 'EDIT', 'WRITE',
      'REJECT', 'MANAGE', 'CANCEL', 'SUBMIT'
    ];
    const list = Array.from(actions);
    list.sort((a, b) => {
      const idxA = preferredOrder.indexOf(a);
      const idxB = preferredOrder.indexOf(b);
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      return a.localeCompare(b);
    });

    return list;
  }, [matrixRows]);

  // Sorted rows
  const sortedFilteredRows = useMemo(() => {
    const rows = [...filteredRows];
    rows.sort((a, b) => {
      // Primary sort: GroupBy field (ensures groups stay together and are ordered)
      if (groupBy === 'employee') {
        const primary = sortBy === 'empNo'
          ? a.employeeId.localeCompare(b.employeeId, undefined, { numeric: true, sensitivity: 'base' })
          : a.userName.localeCompare(b.userName, undefined, { numeric: true, sensitivity: 'base' });
        if (primary !== 0) return primary;
      } else if (groupBy === 'module') {
        const primary = a.module.localeCompare(b.module, undefined, { numeric: true, sensitivity: 'base' });
        if (primary !== 0) return primary;
      } else if (groupBy === 'resource') {
        const primary = a.resource.localeCompare(b.resource, undefined, { numeric: true, sensitivity: 'base' });
        if (primary !== 0) return primary;
      }

      // Secondary sort: SortBy field
      let secondary = 0;
      if (sortBy === 'empNo') {
        secondary = a.employeeId.localeCompare(b.employeeId, undefined, { numeric: true, sensitivity: 'base' });
      } else if (sortBy === 'module') {
        secondary = a.module.localeCompare(b.module, undefined, { numeric: true, sensitivity: 'base' }) ||
                    a.resource.localeCompare(b.resource, undefined, { numeric: true, sensitivity: 'base' });
      } else if (sortBy === 'resource') {
        secondary = a.resource.localeCompare(b.resource, undefined, { numeric: true, sensitivity: 'base' }) ||
                    a.userName.localeCompare(b.userName, undefined, { numeric: true, sensitivity: 'base' });
      } else {
        // default: employee name
        secondary = a.userName.localeCompare(b.userName, undefined, { numeric: true, sensitivity: 'base' });
      }
      
      if (secondary !== 0) return secondary;

      // Final stability sort to keep rows consistent
      return a.userName.localeCompare(b.userName, undefined, { numeric: true, sensitivity: 'base' }) ||
             a.employeeId.localeCompare(b.employeeId, undefined, { numeric: true, sensitivity: 'base' }) ||
             a.module.localeCompare(b.module, undefined, { numeric: true, sensitivity: 'base' }) ||
             a.resource.localeCompare(b.resource, undefined, { numeric: true, sensitivity: 'base' });
    });
    return rows;
  }, [filteredRows, sortBy, groupBy]);

  // Grouped rows for rendering
  const groupedRows = useMemo(() => {
    if (!groupBy) return null;
    
    // Using a Map to preserve the sorted order of groups as they appear in sortedFilteredRows
    const groupsMap = new Map();
    
    sortedFilteredRows.forEach(row => {
      const key = groupBy === 'employee' ? `${row.userName} (${row.employeeId})`
        : groupBy === 'module' ? row.module
        : row.resource;
        
      if (!groupsMap.has(key)) {
        groupsMap.set(key, []);
      }
      groupsMap.get(key).push(row);
    });
    
    return Array.from(groupsMap.entries());
  }, [sortedFilteredRows, groupBy]);

  // Paginated rows (only used when groupBy is off)
  const paginatedRows = useMemo(() => {
    const startIndex = (page - 1) * limit;
    return sortedFilteredRows.slice(startIndex, startIndex + limit);
  }, [sortedFilteredRows, page, limit]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / limit));

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, resourceSearch, moduleFilter, sortBy, groupBy, limit]);

  const loadingPercentage = accountsLoading
    ? (accountsLoadedCount > 0 ? Math.min(90, Math.round(accountsLoadedCount / Math.max(accountsLoadedCount, 50) * 40)) : 5)
    : (allUsers.length > 0
      ? Math.round((resolvedCount / allUsers.length) * 100)
      : 100);

  if ((accountsLoading || loadingRoles) && matrixRows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg border border-gray-200">
        <svg className="w-10 h-10 text-indigo-600 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span className="text-xs font-semibold text-gray-500 mt-3">
          {accountsLoading
            ? `Loading accounts from server${accountsLoadedCount ? ` (${accountsLoadedCount})` : ''}...`
            : 'Initializing role database cache...'}
        </span>
        <button
          type="button"
          onClick={onBack}
          className="mt-4 text-xs font-semibold text-indigo-600 hover:text-indigo-800"
        >
          ← Back to report
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 relative overflow-hidden">
      {/* Loading Progress Bar at the top of the matrix */}
      {!isFullyLoaded && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-gray-100 z-20">
          <div 
            className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 transition-all duration-300"
            style={{ width: `${loadingPercentage}%` }}
          />
        </div>
      )}

      {/* Header bar */}
      <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-bold text-gray-900">All Employees Permission Matrix</h2>
            {!isFullyLoaded && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 animate-pulse border border-indigo-100">
                Loading {loadingPercentage}% ({resolvedCount}/{allUsers.length} accounts)
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">Audit and search permission levels across all registered system accounts</p>
        </div>
        <div className="flex items-center gap-2 self-end sm:self-center">
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 rounded-md transition-all cursor-pointer"
          >
            Export CSV
          </button>
          <button
            onClick={handleExportPDF}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 rounded-md transition-all cursor-pointer"
          >
            Export PDF
          </button>
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-all cursor-pointer"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Users List
          </button>
        </div>
      </div>

      {/* Filters bar */}
      <div className="p-4 bg-gray-50 border-b border-gray-200 grid grid-cols-1 sm:grid-cols-5 gap-3">
        <div>
          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Search Employee</label>
          <input
            type="text"
            placeholder="Name or Emp ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-900"
          />
        </div>
        <div>
          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Search Resource/Permission</label>
          <input
            type="text"
            placeholder="Resource code or name..."
            value={resourceSearch}
            onChange={(e) => setResourceSearch(e.target.value)}
            className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-900"
          />
        </div>
        <div>
          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Module</label>
          <select
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-900"
          >
            <option value="">All Modules</option>
            {modulesList.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Sort By</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-900"
          >
            <option value="employee">Employee Name</option>
            <option value="empNo">Emp No</option>
            <option value="module">Module</option>
            <option value="resource">Resource</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Group By</label>
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-900"
          >
            <option value="">No Grouping</option>
            <option value="employee">Employee</option>
            <option value="module">Module</option>
            <option value="resource">Resource</option>
          </select>
        </div>
      </div>

      {/* Table grid */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Emp No</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Employee Name</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Module</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Resource</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Permission Name</th>
              {actionColumns.map((action) => (
                <th key={action} className="py-2 text-center" style={{ width: '24px', minWidth: '24px', padding: '0' }}>
                  <div
                    style={{
                      writingMode: 'vertical-rl',
                      transform: 'rotate(180deg)',
                      display: 'inline-block',
                      textAlign: 'center',
                      fontSize: '8px',
                      fontWeight: '700',
                      letterSpacing: '0.04em',
                      color: '#9ca3af',
                      textTransform: 'uppercase',
                      lineHeight: 1,
                      padding: '4px 0',
                    }}
                  >
                    {action}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 text-xs">
            {sortedFilteredRows.length === 0 ? (
              <tr>
                <td colSpan={5 + actionColumns.length} className="px-6 py-10 text-center text-gray-400">
                  {matrixRows.length === 0 ? "Loading initial records..." : "No permission records found matching your filters."}
                </td>
              </tr>
            ) : groupedRows ? (
              // GROUPED mode — render all rows with group header separators (no pagination)
              groupedRows.map(([groupKey, rows]) => (
                <React.Fragment key={groupKey}>
                  {/* Group Header Row */}
                  <tr className="bg-indigo-50/70 border-t-2 border-indigo-100">
                    <td
                      colSpan={5 + actionColumns.length}
                      className="px-4 py-1.5"
                    >
                      <span className="text-[10px] font-bold text-indigo-700 uppercase tracking-widest">
                        {groupBy === 'employee' ? '👤 ' : groupBy === 'module' ? '📦 ' : '🔑 '}
                        {groupKey}
                      </span>
                      <span className="ml-2 text-[9px] text-indigo-400 font-medium">({rows.length} row{rows.length !== 1 ? 's' : ''})</span>
                    </td>
                  </tr>
                  {/* Group rows */}
                  {rows.map((row, index) => (
                    <tr key={`${row.userId}-${row.module}-${row.resource}-${index}`} className="hover:bg-indigo-50/20 transition-colors">
                      <td className="px-4 py-2 font-semibold text-gray-700 whitespace-nowrap">{row.employeeId}</td>
                      <td className="px-4 py-2 font-medium text-gray-900 whitespace-nowrap">{row.userName}</td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">
                          {row.module}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-mono text-[11px] text-gray-600 whitespace-nowrap">{row.resource}</td>
                      <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{row.permissionName}</td>
                      {actionColumns.map((action) => {
                        const hasAction = row.actions[action];
                        return (
                          <td key={action} className="py-2 text-center" style={{ width: '24px', minWidth: '24px', padding: '6px 0' }}>
                            {hasAction ? (
                              <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                            ) : (
                              <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#e5e7eb', flexShrink: 0 }} />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))
            ) : (
              // FLAT paginated mode
              paginatedRows.map((row, index) => (
                <tr key={`${row.userId}-${row.module}-${row.resource}-${index}`} className="hover:bg-indigo-50/20 transition-colors">
                  <td className="px-4 py-2.5 font-semibold text-gray-700 whitespace-nowrap">{row.employeeId}</td>
                  <td className="px-4 py-2.5 font-medium text-gray-900 whitespace-nowrap">{row.userName}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">
                      {row.module}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-gray-600 whitespace-nowrap">{row.resource}</td>
                  <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{row.permissionName}</td>
                  {actionColumns.map((action) => {
                    const hasAction = row.actions[action];
                    return (
                      <td key={action} className="py-2 text-center" style={{ width: '24px', minWidth: '24px', padding: '6px 0' }}>
                        {hasAction ? (
                          <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                        ) : (
                          <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#e5e7eb', flexShrink: 0 }} />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>


      {/* Pagination controls */}
      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
        <div className="text-xs text-gray-500">
          Showing <span className="font-semibold text-gray-800">{filteredRows.length > 0 ? (page - 1) * limit + 1 : 0}</span> to{' '}
          <span className="font-semibold text-gray-800">{Math.min(page * limit, filteredRows.length)}</span> of{' '}
          <span className="font-semibold text-gray-800">{filteredRows.length}</span> rows
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Rows per page:</span>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="px-2 py-1 text-xs border border-gray-300 rounded bg-white text-gray-800 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded border border-gray-300 bg-white text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-xs text-gray-650 px-2 font-medium">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded border border-gray-300 bg-white text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
